import { useEffect, useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import { CheckIcon } from '@heroicons/react/20/solid'
import Cookies from 'universal-cookie'
import { I18n, t } from 'shared/i18n/i18n'

type Language = {
  id: string
  name: string
}

const languages: Language[] = [
  { id: 'en', name: t('language_switcher.english') },
  { id: 'de', name: t('language_switcher.german') },
]

export default function LanguageSwitcher() {
  const [selected, setSelected] = useState(languages[0])

  useEffect(() => {
    for (const language of languages) {
      if (language.id === I18n.locale) {
        setSelected(language)
      }
    }
  }, [setSelected])

  const handleChange = async (value: Language) => {
    const cookies = new Cookies()

    // Available locales check (mimicking I18n.available_locales)
    const availableLocales = ['en', 'de']

    if (availableLocales.includes(value.id)) {
      // Set cookie with same options as Rails controller
      const expiresDate = new Date()
      expiresDate.setMonth(expiresDate.getMonth() + 6) // 6 months from now

      cookies.set('locale', value.id, {
        expires: expiresDate,
        domain: window.location.hostname.includes('.')
          ? `.${window.location.hostname.split('.').slice(-2).join('.')}`
          : undefined, // Handle subdomain support
        secure: window.location.protocol === 'https:',
        sameSite: 'lax',
        path: '/',
      })

      setSelected(value)
      // Reload the page to apply locale change
      window.location.reload()
    } else {
      console.error('Invalid language selected')
    }
  }

  return (
    <Listbox value={selected} onChange={handleChange}>
      <div className="relative mt-2">
        <ListboxButton className="grid cursor-default grid-cols-1 rounded-md bg-white py-1.5 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6">
          <span className="col-start-1 row-start-1 truncate pr-6">{selected.name}</span>
          <ChevronUpDownIcon
            aria-hidden="true"
            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
          />
        </ListboxButton>

        <ListboxOptions
          transition
          anchor="top"
          className="absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
        >
          {languages.map((person) => (
            <ListboxOption
              key={person.id}
              value={person}
              className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
            >
              <span className="block truncate font-normal group-data-selected:font-semibold">{person.name}</span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-not-data-selected:hidden group-data-focus:text-white">
                <CheckIcon aria-hidden="true" className="size-5" />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}
